import { motion } from 'framer-motion';
import {
  CheckCircle,
  ChevronDown,
  Edit,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Wallet,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useAttendance, useAuth } from '@/hooks';
import { userService } from '@/services';
import { useAuthStore } from '@/store';
import type { BankDetails } from '@/types';
import { extractErrorMessage, formatNaira } from '@/utils';
import { cn } from '@/utils/cn';
import { NIGERIAN_BANKS } from '@/utils/nigerian-banks';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function PayChecks() {
  const { user } = useAuth();
  const { fetchCurrentUser } = useAuthStore();
  const { paymentHistory, currentPayment, isLoading, fetchPaymentRecord } = useAttendance(false);
  const [isEditingBankDetails, setIsEditingBankDetails] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankDetails, setBankDetails] = useState<BankDetails>({
    accountName: '',
    accountNumber: '',
    bankName: '',
  });
  const [bankDetailsErrors, setBankDetailsErrors] = useState<{
    accountName?: string;
    accountNumber?: string;
    bankName?: string;
  }>({});
  const [bankDropdownOpen, setBankDropdownOpen] = useState(false);
  const bankDropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [showFinancialData, setShowFinancialData] = useState(false);
  const [showPaymentHistoryData, setShowPaymentHistoryData] = useState(false);

  useEffect(() => {
    fetchPaymentRecord();
    if (user?.bankDetails) {
      setBankDetails(user.bankDetails);
    }
  }, [fetchPaymentRecord, user?.bankDetails]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        bankDropdownOpen &&
        !target.closest('.bank-dropdown-container') &&
        !target.closest('.bank-dropdown-portal')
      ) {
        setBankDropdownOpen(false);
        setDropdownPosition(null);
      }
    };

    if (bankDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [bankDropdownOpen]);

  useEffect(() => {
    if (bankDropdownOpen && bankDropdownRef.current) {
      const rect = bankDropdownRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    } else {
      setDropdownPosition(null);
    }
  }, [bankDropdownOpen]);

  const validateAccountName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Account name is required';
    }
    const nameParts = name.trim().split(/\s+/);
    if (nameParts.length < 2) {
      return 'Account name must include at least first name and last name';
    }
    const namePattern = /^[A-Za-z]{2,}$/;
    for (const part of nameParts) {
      if (!namePattern.test(part)) {
        return 'Each name part must be at least 2 characters and contain only letters';
      }
    }
    return undefined;
  };

  const validateAccountNumber = (number: string): string | undefined => {
    if (!number.trim()) {
      return 'Account number is required';
    }
    if (!/^\d{10}$/.test(number)) {
      return 'Account number must be exactly 10 numeric digits';
    }
    return undefined;
  };

  const validateBankName = (name: string): string | undefined => {
    if (!name.trim()) {
      return 'Bank name is required';
    }
    return undefined;
  };

  const handleEditBankDetails = () => {
    setIsEditingBankDetails(true);
    setBankDetailsErrors({});
    setBankDetails({
      accountName: user?.bankDetails?.accountName || '',
      accountNumber: user?.bankDetails?.accountNumber || '',
      bankName: user?.bankDetails?.bankName || '',
    });
  };

  const handleCancelEdit = () => {
    setIsEditingBankDetails(false);
    setBankDropdownOpen(false);
    setBankDetailsErrors({});
    setBankDetails({
      accountName: user?.bankDetails?.accountName || '',
      accountNumber: user?.bankDetails?.accountNumber || '',
      bankName: user?.bankDetails?.bankName || '',
    });
  };

  const handleSaveBankDetails = async () => {
    const errors: typeof bankDetailsErrors = {};
    const accountNameError = validateAccountName(bankDetails.accountName);
    const accountNumberError = validateAccountNumber(bankDetails.accountNumber);
    const bankNameError = validateBankName(bankDetails.bankName);

    if (accountNameError) errors.accountName = accountNameError;
    if (accountNumberError) errors.accountNumber = accountNumberError;
    if (bankNameError) errors.bankName = bankNameError;

    setBankDetailsErrors(errors);

    if (Object.keys(errors).length > 0) {
      toast.error('Please fix the validation errors');
      return;
    }

    setSavingBankDetails(true);
    try {
      await userService.updateBankDetails(bankDetails);
      await fetchCurrentUser();
      toast.success('Bank details updated successfully');
      setIsEditingBankDetails(false);
      setBankDetailsErrors({});
      setBankDropdownOpen(false);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to update bank details'));
    } finally {
      setSavingBankDetails(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] space-y-3">
        <div className="w-10 h-10 border-4 border-neolab-green/20 border-t-neolab-green rounded-full animate-spin" />
        <p className="text-slate-400 font-bold text-sm animate-pulse">Loading payment history...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
      >
        <div className="px-6 py-4 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-white dark:bg-neolab-forest shadow-sm">
              <Wallet className="w-5 h-5 text-neolab-green" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                Bank Details
              </h2>
              <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                Payment account information
              </p>
            </div>
          </div>
          {!isEditingBankDetails && (
            <button
              type="button"
              onClick={handleEditBankDetails}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-neolab-green text-white font-black text-xs hover:bg-neolab-green/90 transition-all cursor-pointer"
            >
              <Edit className="w-3.5 h-3.5" />
              Edit
            </button>
          )}
        </div>

        <div className="p-6">
          {isEditingBankDetails ? (
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="account-name"
                  className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-1 block"
                >
                  Account Name <span className="text-rose-500">*</span>
                </label>
                <input
                  id="account-name"
                  type="text"
                  value={bankDetails.accountName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBankDetails({ ...bankDetails, accountName: value });
                    const error = validateAccountName(value);
                    setBankDetailsErrors({
                      ...bankDetailsErrors,
                      accountName: error,
                    });
                  }}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                    bankDetailsErrors.accountName
                      ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                      : 'focus:ring-neolab-green/20',
                  )}
                  placeholder="Enter full name (e.g., John Doe or John Michael Doe)"
                />
                {bankDetailsErrors.accountName && (
                  <p className="text-xs font-bold text-rose-500 mt-1">
                    {bankDetailsErrors.accountName}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="account-number"
                  className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-1 block"
                >
                  Account Number <span className="text-rose-500">*</span>
                </label>
                <input
                  id="account-number"
                  type="text"
                  value={bankDetails.accountNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setBankDetails({ ...bankDetails, accountNumber: value });
                    const error = validateAccountNumber(value);
                    setBankDetailsErrors({
                      ...bankDetailsErrors,
                      accountNumber: error,
                    });
                  }}
                  maxLength={10}
                  className={cn(
                    'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white',
                    bankDetailsErrors.accountNumber
                      ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                      : 'focus:ring-neolab-green/20',
                  )}
                  placeholder="Enter 10-digit account number"
                />
                {bankDetailsErrors.accountNumber && (
                  <p className="text-xs font-bold text-rose-500 mt-1">
                    {bankDetailsErrors.accountNumber}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="bank-name-select"
                  className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase mb-1 block"
                >
                  Bank Name <span className="text-rose-500">*</span>
                </label>
                <div className="relative bank-dropdown-container" ref={bankDropdownRef}>
                  <button
                    id="bank-name-select"
                    type="button"
                    onClick={() => setBankDropdownOpen(!bankDropdownOpen)}
                    className={cn(
                      'w-full px-4 py-3 rounded-xl bg-neolab-green/5 dark:bg-white/5 border-none focus:ring-2 outline-none text-sm font-black text-slate-900 dark:text-white flex items-center justify-between cursor-pointer',
                      bankDetailsErrors.bankName
                        ? 'focus:ring-rose-500/20 border-2 border-rose-500/50'
                        : 'focus:ring-neolab-green/20',
                    )}
                  >
                    <span className={bankDetails.bankName ? '' : 'text-slate-400'}>
                      {bankDetails.bankName || 'Select bank'}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 transition-transform',
                        bankDropdownOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  {bankDropdownOpen &&
                    dropdownPosition &&
                    createPortal(
                      <div
                        className="bank-dropdown-portal fixed max-h-[300px] overflow-y-auto bg-white dark:bg-neolab-forest border border-slate-200 dark:border-slate-700 rounded-lg shadow-2xl z-[100]"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            setBankDropdownOpen(false);
                            setDropdownPosition(null);
                          }
                        }}
                        role="listbox"
                        tabIndex={-1}
                      >
                        {NIGERIAN_BANKS.map((bank) => (
                          <button
                            key={bank.code}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setBankDetails((prev) => ({ ...prev, bankName: bank.name }));
                              setBankDetailsErrors((prev) => ({
                                ...prev,
                                bankName: undefined,
                              }));
                              setBankDropdownOpen(false);
                              setDropdownPosition(null);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-900 dark:text-white font-medium text-xs cursor-pointer transition-colors"
                          >
                            {bank.name}
                          </button>
                        ))}
                      </div>,
                      document.body,
                    )}
                </div>
                {bankDetailsErrors.bankName && (
                  <p className="text-xs font-bold text-rose-500 mt-1">
                    {bankDetailsErrors.bankName}
                  </p>
                )}
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  disabled={savingBankDetails}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-black text-xs hover:opacity-90 transition-all disabled:opacity-50 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveBankDetails}
                  disabled={
                    savingBankDetails ||
                    !!bankDetailsErrors.accountName ||
                    !!bankDetailsErrors.accountNumber ||
                    !!bankDetailsErrors.bankName ||
                    !bankDetails.accountName ||
                    !bankDetails.accountNumber ||
                    !bankDetails.bankName
                  }
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 rounded-xl bg-neolab-green text-white font-black text-xs hover:bg-neolab-green/90 transition-all disabled:opacity-50 cursor-pointer',
                  )}
                >
                  {savingBankDetails ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Save className="w-3.5 h-3.5" />
                  )}
                  Save
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 rounded-xl bg-neolab-green/5 dark:bg-white/5">
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Account Name
                </p>
                <p className="text-lg font-black text-slate-900 dark:text-white">
                  {user?.bankDetails?.accountName || 'Not set'}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-blue-500/5 dark:bg-blue-500/10">
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Account Number
                </p>
                <p className="text-lg font-black text-slate-900 dark:text-white">
                  {user?.bankDetails?.accountNumber || 'Not set'}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-emerald-500/5 dark:bg-emerald-500/10">
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Bank Name
                </p>
                <p className="text-lg font-black text-slate-900 dark:text-white">
                  {user?.bankDetails?.bankName || 'Not set'}
                </p>
              </div>
            </div>
          )}
        </div>
      </motion.div>

      {currentPayment && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-white dark:bg-neolab-forest shadow-sm">
                <Wallet className="w-5 h-5 text-neolab-green" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  Monthly Payment
                </h2>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                  Current month
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowFinancialData(!showFinancialData)}
              className="p-2 rounded-lg hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
              title={showFinancialData ? 'Hide financial data' : 'Show financial data'}
            >
              {showFinancialData ? (
                <Eye className="w-5 h-5 text-neolab-green" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-neolab-green/5 dark:bg-white/5">
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Salary
                </p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">
                  {showFinancialData ? formatNaira(currentPayment.baseSalary) : '••••••'}
                </p>
              </div>

              {currentPayment.allowance > 0 && (
                <div className="p-4 rounded-xl bg-blue-500/5 dark:bg-blue-500/10">
                  <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                    Allowance
                  </p>
                  <p className="text-2xl font-black text-slate-900 dark:text-white">
                    {showFinancialData ? formatNaira(currentPayment.allowance) : '••••••'}
                  </p>
                </div>
              )}

              <div className="p-4 rounded-xl bg-emerald-500/5 dark:bg-emerald-500/10">
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Normal Monthly Pay
                </p>
                <p className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
                  {showFinancialData ? formatNaira(currentPayment.normalMonthlyPay) : '••••••'}
                </p>
              </div>

              <div
                className={`p-4 rounded-xl ${currentPayment.netIncome === currentPayment.normalMonthlyPay ? 'bg-emerald-500/5 dark:bg-green-500/10' : 'bg-rose-500/5 dark:bg-rose-500/10'}`}
              >
                <p className="text-[11px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.1em] mb-2">
                  Current Month's Income
                </p>
                <p
                  className={`text-2xl font-black ${currentPayment.netIncome === currentPayment.normalMonthlyPay ? 'text-emerald-600 dark:text-green-400' : 'text-rose-600 dark:text-rose-400'}`}
                >
                  {showFinancialData ? formatNaira(currentPayment.netIncome) : '••••••'}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {paymentHistory.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
        >
          <div className="px-6 py-4 border-b border-neolab-green/5 dark:border-white/5 flex items-center justify-between bg-neolab-green/[0.02] dark:bg-white/5">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-white dark:bg-neolab-forest shadow-sm">
                <Wallet className="w-5 h-5 text-neolab-green" />
              </div>
              <div>
                <h2 className="text-xl font-black text-slate-900 dark:text-white tracking-tight">
                  Payment History
                </h2>
                <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
                  All payment records
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowPaymentHistoryData(!showPaymentHistoryData)}
              className="p-2 rounded-lg hover:bg-neolab-green/10 dark:hover:bg-white/10 transition-colors"
              title={showPaymentHistoryData ? 'Hide financial data' : 'Show financial data'}
            >
              {showPaymentHistoryData ? (
                <Eye className="w-5 h-5 text-neolab-green" />
              ) : (
                <EyeOff className="w-5 h-5 text-slate-400" />
              )}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neolab-green/[0.02] dark:bg-white/5 border-b border-neolab-green/10 dark:border-white/10">
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Month/Year
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Base Salary
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Allowance
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Normal Total Pay
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Total Deductions
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest">
                    Net Income
                  </th>
                  <th className="p-4 text-xs font-black text-slate-500 uppercase tracking-widest text-center">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment) => (
                  <tr
                    key={payment._id}
                    className="border-b border-neolab-green/[0.02] dark:border-white/5 hover:bg-neolab-green/[0.02] dark:hover:bg-white/5 transition-colors"
                  >
                    <td className="p-4 font-black text-slate-900 dark:text-white">
                      {MONTHS[payment.month - 1]} {payment.year}
                    </td>
                    <td className="p-4 font-black text-slate-900 dark:text-white">
                      {showPaymentHistoryData ? formatNaira(payment.baseSalary) : '••••••'}
                    </td>
                    <td className="p-4 font-black text-slate-900 dark:text-white">
                      {showPaymentHistoryData ? formatNaira(payment.allowance) : '••••••'}
                    </td>
                    <td className="p-4 font-black text-emerald-600 dark:text-emerald-400">
                      {showPaymentHistoryData ? formatNaira(payment.normalMonthlyPay) : '••••••'}
                    </td>
                    <td className="p-4 font-black text-rose-600 dark:text-rose-400">
                      {showPaymentHistoryData ? formatNaira(payment.totalDeductions) : '••••••'}
                    </td>
                    <td className="p-4 font-black text-slate-900 dark:text-white">
                      {showPaymentHistoryData ? formatNaira(payment.netIncome) : '••••••'}
                    </td>
                    <td className="p-4 text-center">
                      {payment.paid ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-xs font-black uppercase tracking-wider">
                          <CheckCircle className="w-3 h-3" />
                          Paid
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-black uppercase tracking-wider">
                          Pending
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="rounded-xl glass-card dark:glass-card-dark shadow-sm overflow-hidden"
        >
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wallet className="w-10 h-10 text-slate-300 dark:text-slate-600" />
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-bold text-lg">
              No payment records found
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default PayChecks;
